import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Moduli from "@/pages/Moduli";
import ModuloDetail from "@/pages/ModuloDetail";
import Lezione from "@/pages/Lezione";
import Simulazioni from "@/pages/Simulazioni";
import Ripasso from "@/pages/Ripasso";
import Esame from "@/pages/Esame";
import Risultati from "@/pages/Risultati";
import Sblocca from "@/pages/Sblocca";
import Offerta from "@/pages/Offerta";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Admin from "@/pages/Admin";
import Info from "@/pages/Info";

const FullLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-white">
    <div className="font-mono text-sm text-[color:var(--text-2)] tracking-widest uppercase">Caricamento…</div>
  </div>
);

function Protected({ children, admin = false }) {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (admin && !user.is_admin) return <Navigate to="/" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (user) return <Navigate to="/" replace />;
  return children;
}

// Come Protected, ma se l'utente non è loggato reindirizza a /login?next=<percorso+utm>,
// così dopo il login si torna a /offerta conservando i parametri utm.
function ProtectedNext({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullLoader />;
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
            <Route path="/password-dimenticata" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
            <Route path="/reset-password" element={<ResetPassword />} />
            {/* Performance mode: no app chrome */}
            <Route path="/esame" element={<Protected><Esame /></Protected>} />
            {/* Ritorno dal pagamento: PUBBLICHE — devono funzionare anche senza sessione attiva */}
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route element={<Protected><Layout /></Protected>}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/moduli" element={<Moduli />} />
              <Route path="/moduli/:moduleId" element={<ModuloDetail />} />
              <Route path="/lezione/:lessonId" element={<Lezione />} />
              <Route path="/simulazioni" element={<Simulazioni />} />
              <Route path="/ripasso" element={<Ripasso />} />
              <Route path="/risultati" element={<Risultati />} />
              <Route path="/sblocca" element={<Sblocca />} />
              <Route path="/info" element={<Info />} />
              <Route path="/admin" element={<Protected admin><Admin /></Protected>} />
            </Route>
            {/* /offerta: login con ritorno e utm preservati (ProtectedNext) */}
            <Route element={<ProtectedNext><Layout /></ProtectedNext>}>
              <Route path="/offerta" element={<Offerta />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
